// AgentCardPage — la tarjeta digital pública, en app.deskte.com/t/<slug>.
// SIN autenticación: la dirección es la llave.
//
// Lissette, 2026-08-26: lo que se entiende por tarjeta digital es un ENLACE que
// se manda por WhatsApp y un CÓDIGO QR que el cliente escanea. Esto es lo que
// se abre al otro lado de los dos.
//
// La página se diseña para el TELÉFONO primero. Casi nadie abre una tarjeta de
// contacto en una computadora: llega por WhatsApp o de escanear un QR, y en las
// dos se abre en el móvil.
import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { getPublicAgentCard } from '../api.js';
import { telHref, waHref } from '../phoneLinks.js';
// La forma del logo que eligió el agente en Mi Perfil (2026-09-23).
import { estiloDelLogoEnTarjeta } from '../formaDelLogo.js';

const REDES = {
  facebook: { label: 'Facebook', icon: 'f', bg: '#1877f2' },
  instagram: { label: 'Instagram', icon: '◎', bg: '#e1306c' },
  linkedin: { label: 'LinkedIn', icon: 'in', bg: '#0a66c2' },
  tiktok: { label: 'TikTok', icon: '♪', bg: '#111' },
};

/** El .vcf que se descarga con "Guardar mi contacto". */
function buildVCard(c) {
  const esc = (s) => String(s || '').replace(/[\\;,]/g, (m) => '\\' + m).replace(/\n/g, '\\n');
  const partes = String(c.name || '').trim().split(/\s+/);
  const first = partes.shift() || c.name || '';
  const last = partes.join(' ');
  const L = ['BEGIN:VCARD', 'VERSION:3.0', `N:${esc(last)};${esc(first)};;;`, `FN:${esc(c.name)}`];
  if (c.title) L.push(`TITLE:${esc(c.title)}`);
  if (c.company) L.push(`ORG:${esc(c.company)}`);
  if (c.phone) L.push(`TEL;TYPE=CELL:${esc(c.phone)}`);
  if (c.officePhone) L.push(`TEL;TYPE=WORK,VOICE:${esc(c.officePhone)}`);
  if (c.email) L.push(`EMAIL;TYPE=INTERNET:${esc(c.email)}`);
  if (c.email2) L.push(`EMAIL;TYPE=INTERNET:${esc(c.email2)}`);
  if (c.website) L.push(`URL:${esc(c.website)}`);
  for (const s of c.social || []) L.push(`X-SOCIALPROFILE;TYPE=${s.red}:${esc(s.url)}`);
  if (c.license) L.push(`NOTE:${esc('Licencias: ' + c.license)}`);
  L.push('END:VCARD');
  // El BOM y el salto de línea final son E-142: sin ellos el archivo se
  // descarga bien y Contactos de Windows no lo abre. Aquí importa igual o más,
  // porque este .vcf lo abre EL CLIENTE y no hay a quién preguntarle.
  return '\uFEFF' + L.join('\r\n') + '\r\n';
}

export function AgentCardPage({ slug }) {
  const [card, setCard] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let off = false;
    getPublicAgentCard(slug)
      .then((d) => { if (!off) setCard(d); })
      .catch((e) => { if (!off) setError(e.message || String(e)); });
    return () => { off = true; };
  }, [slug]);

  const guardarContacto = () => {
    try {
      const blob = new Blob([buildVCard(card)], { type: 'text/vcard;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (card.name || 'contacto').replace(/[^a-z0-9]+/gi, '_') + '.vcf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { /* el navegador lo bloqueó; los botones de llamar y escribir siguen ahí */ }
  };

  const compartir = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: card.name, url }); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try { await navigator.clipboard?.writeText(url); } catch { /* sin portapapeles */ }
    setMsg('Enlace copiado');
    setTimeout(() => setMsg(''), 2000);
  };

  if (error) {
    return (
      <Marco>
        <AlertCircle size={36} style={{ color: '#e11d48', marginBottom: 10 }} />
        <h2 style={{ margin: '6px 0', fontSize: 20 }}>Esta tarjeta no está disponible</h2>
        <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
          El enlace puede estar mal escrito, o su dueño la desactivó. Si te la pasó tu agente, pídele que te la mande de nuevo.
        </p>
      </Marco>
    );
  }
  if (!card) {
    return <Marco><Loader2 size={26} className="dkt-spin" style={{ color: '#0a558c' }} /></Marco>;
  }

  const tel = telHref(card.phone);
  const telOficina = telHref(card.officePhone);
  // WhatsApp y fax (Lissette 2026-08-29). Solo salen si el agente los escribio
  // en su perfil: el servidor manda cadena vacia cuando no estan, y aqui eso
  // es una linea que no se pinta.
  const wa = waHref(card.whatsapp);
  const btn = (bg, color) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    width: '100%', padding: '13px 16px', borderRadius: 12, border: 'none',
    background: bg, color, fontSize: 15.5, fontWeight: 800, cursor: 'pointer',
    textDecoration: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  });

  return (
    <Marco ancho>
      {card.logo
        ? <img src={card.logo} alt="" style={estiloDelLogoEnTarjeta(card.logoShape)} />
        : null}

      {card.photo
        ? <img src={card.photo} alt="" style={{ width: 104, height: 104, borderRadius: '50%', objectFit: 'cover', border: '3px solid #fff', boxShadow: '0 4px 18px rgba(10,85,140,.22)' }} />
        : <div style={{ width: 104, height: 104, borderRadius: '50%', background: 'linear-gradient(135deg,#0a558c,#0891b2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38, fontWeight: 800 }}>
            {(card.name || '?').trim().charAt(0).toUpperCase()}
          </div>}

      <h1 style={{ margin: '14px 0 2px', fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{card.name}</h1>
      {card.title && <div style={{ color: '#0a558c', fontWeight: 700, fontSize: 14.5 }}>{card.title}</div>}
      {card.company && <div style={{ color: '#64748b', fontSize: 14, marginTop: 2 }}>{card.company}</div>}
      {card.bio && <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.55, margin: '12px 0 0', fontStyle: 'italic' }}>“{card.bio}”</p>}

      {/* Las acciones ANTES que los datos, y en botones grandes. Quien abre
          esto en el celular quiere llamar o guardar el contacto — no leer una
          ficha. Poner primero la lista de datos obliga a bajar para hacer lo
          único que se viene a hacer. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, width: '100%', marginTop: 20 }}>
        {tel && <a href={`tel:${tel}`} style={btn('linear-gradient(135deg,#0a558c,#0891b2)', '#fff')}>📞 Llamar</a>}
        {tel && <a href={`sms:${tel}`} style={btn('#ecfeff', '#0a558c')}>💬 Enviar mensaje</a>}
        {card.email && <a href={`mailto:${card.email}`} style={btn('#ecfeff', '#0a558c')}>✉️ Escribir</a>}
        <button type="button" onClick={guardarContacto} style={btn('#0f172a', '#fff')}>📇 Guardar mi contacto</button>
        <button type="button" onClick={compartir} style={btn('#f1f5f9', '#334155')}>🔗 Compartir esta tarjeta</button>
      </div>
      {msg && <div style={{ marginTop: 10, fontSize: 13, fontWeight: 700, color: '#0a558c' }}>{msg}</div>}

      {(card.social || []).length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
          {card.social.map((s) => {
            const r = REDES[s.red] || { label: s.red, icon: '•', bg: '#64748b' };
            // `rel` no es opcional en una página pública: sin `noopener`, la
            // pestaña que se abre puede reescribir ésta desde el otro sitio.
            return (
              <a key={s.red} href={s.url} target="_blank" rel="noopener noreferrer" title={r.label} aria-label={r.label}
                style={{ width: 42, height: 42, borderRadius: '50%', background: r.bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, textDecoration: 'none' }}>
                {r.icon}
              </a>
            );
          })}
        </div>
      )}

      <div style={{ width: '100%', marginTop: 22, paddingTop: 16, borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 7, fontSize: 13.5, color: '#475569', textAlign: 'left' }}>
        {card.email && <Dato icono="📧" texto={card.email} href={`mailto:${card.email}`} />}
        {card.email2 && <Dato icono="📧" texto={card.email2} href={`mailto:${card.email2}`} />}
        {tel && <Dato icono="📱" texto={card.phone} href={`tel:${tel}`} />}
        {telOficina && <Dato icono="☎️" texto={card.officePhone} href={`tel:${telOficina}`} />}
        {/* El WhatsApp abre la conversación si el número se puede marcar; si no
            —un número a medio escribir, una extensión— se enseña igual, como
            texto. Un dato visible sin enlace es mejor que un enlace que abre
            un chat con quien no es. El fax nunca lleva enlace: no se llama. */}
        {card.whatsapp && (wa
          ? <Dato icono="💬" texto={card.whatsapp} href={`https://wa.me/${wa}`} externo />
          : <Dato icono="💬" texto={card.whatsapp} />)}
        {card.fax && <Dato icono="📠" texto={`Fax: ${card.fax}`} />}
        {card.website && <Dato icono="🌐" texto={card.website} href={/^https?:\/\//i.test(card.website) ? card.website : `https://${card.website}`} externo />}
        {card.license && <Dato icono="📋" texto={`Licencias: ${card.license}`} />}
      </div>

      <div style={{ marginTop: 20, fontSize: 11.5, color: '#94a3b8' }}>Tarjeta digital · Deskte</div>
    </Marco>
  );
}

function Dato({ icono, texto, href, externo }) {
  const cuerpo = <><span style={{ flexShrink: 0 }}>{icono}</span><span style={{ wordBreak: 'break-word' }}>{texto}</span></>;
  const st = { display: 'flex', gap: 8, alignItems: 'flex-start', color: 'inherit', textDecoration: 'none' };
  if (!href) return <div style={st}>{cuerpo}</div>;
  return <a href={href} style={st} {...(externo ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{cuerpo}</a>;
}

function Marco({ children, ancho }) {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg,#f8fafc,#e0f2fe)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px', fontFamily: 'system-ui,-apple-system,Segoe UI,Roboto,sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 10px 40px rgba(15,23,42,.10)', padding: ancho ? '30px 24px 26px' : '40px 26px', width: '100%', maxWidth: 420, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', boxSizing: 'border-box' }}>
        {children}
      </div>
    </div>
  );
}
